import { useEffect, useRef, useState } from "react";
import { Button, CalendarCell, CalendarGrid, CalendarGridBody, CalendarGridHeader, CalendarHeaderCell, RangeCalendar } from "react-aria-components";
import { parseDate, CalendarDate } from "@internationalized/date";
import "./dateRangeCalendar.css";
import { I18nProvider, useDateFormatter } from "react-aria";
import { _, _any, Locale } from "i18n";
import { useOnClickOutside } from "usehooks-ts";
import { ArrowLeft, ArrowRight } from "ui-kit/src/icons";

interface BaseRangeCalendarProps {
	start: string;
	end: string;
	onChange: (start: string, end: string) => void;
	placeholder?: string;
	apiFormat?: boolean;
	iconArrow?: boolean;
}

interface DateRangeCalendarProps extends BaseRangeCalendarProps {
	locale: Locale;
	placeholder?: string;
}

function BaseRangeCalendar({ start, end, onChange, placeholder, apiFormat = false, iconArrow = false }: BaseRangeCalendarProps) {
	const dateFormatter = useDateFormatter({
		//dateStyle: "short",
		month: "long",
		year: "numeric",
	});

	const currentDate = new Date().toISOString().split("T")[0];
	const containerRef = useRef<HTMLDivElement>(null);
	const [show, setShow] = useState(false);
	const [range, setRange] = useState({
		start: parseDate(currentDate),
		end: parseDate(currentDate),
	});

	const dayStrings: Record<string, string> = {
		Mon: "datepicker.days_short.monday",
		Tue: "datepicker.days_short.tuesday",
		Wed: "datepicker.days_short.wednesday",
		Thu: "datepicker.days_short.thursday",
		Fri: "datepicker.days_short.friday",
		Sat: "datepicker.days_short.saturday",
		Sun: "datepicker.days_short.sunday",
	};

	const getDayString = (day: string) => {
		return dayStrings[day] || day;
	};

	useEffect(() => {
		if (!start && !end) {
			setRange({
				start: parseDate(currentDate),
				end: parseDate(currentDate),
			});
		}
	}, [start, end, currentDate]);

	useOnClickOutside(containerRef, () => {
		setShow(false);
	});

	const formatDate = (date: CalendarDate) => {
		return `${date.day}/${date.month}/${date.year}`;
	};

	const formatOutputDate = (date: CalendarDate) => {
		if (!apiFormat) {
			return formatDate(date);
		}
		return date.toString();
	};

	const handleApply = () => {
		onChange(formatOutputDate(range.start), formatOutputDate(range.end));
		setShow(false);
	};

	const handleCancel = () => {
		onChange("", "");
		setRange({
			start: parseDate(currentDate),
			end: parseDate(currentDate),
		});
		setShow(false);
	};

	return (
		<div ref={containerRef}>
			<div
				className="calendarInput-container"
				onClick={() => {
					setShow(!show);
				}}
			>
				{iconArrow && !(start && end) && <span className="icon-new icon-select-arrow-down" />}
				<input
					readOnly
					placeholder={placeholder}
					value={start && `${start} - ${end}`}
					className={`calendarInput ${start && end ? "hasValue" : ""}`}
				/>

				{start && end && <span
					className="icon-new icon-cross icon-white"
					onClick={(e) => {
						e.stopPropagation();
						handleCancel()
					}} />}
			</div>
			{show && (
				<div className="calendarContainer">
					{/* rome-ignore lint/style/useSelfClosingElements: <explanation> */}
					<RangeCalendar
						//@ts-ignore
						value={range}
						//@ts-ignore
						onChange={setRange}
						visibleDuration={{ months: 2 }}
						pageBehavior="single"
						children={({ state: { visibleRange, timeZone } }) => {
							return (
								<>
									<header className="calendarHeading">
										<Button slot="previous">
											<ArrowLeft />
										</Button>
										{dateFormatter.formatRange(visibleRange.start.toDate(timeZone), visibleRange.end.toDate(timeZone))}
										<Button slot="next">
											<ArrowRight />
										</Button>
									</header>
									<div style={{ display: "flex", gap: 30, overflow: "auto", flexWrap: "wrap", justifyContent: "center" }}>
										{/* <CalendarGrid weekdayStyle="short">{(date) => <CalendarCell date={date} />}</CalendarGrid> */}
										<CalendarGrid weekdayStyle="short">
											<CalendarGridHeader>
												{(day, ...props) => {
													const mappedDay = getDayString(day);
													return (
														<CalendarHeaderCell style={{ color: "var(--blue)" }}>{_any(mappedDay)}</CalendarHeaderCell>
													);
												}}
											</CalendarGridHeader>
											<CalendarGridBody>{(date) => <CalendarCell date={date} />}</CalendarGridBody>
										</CalendarGrid>
										<CalendarGrid weekdayStyle="short" offset={{ months: 1 }}>
											<CalendarGridHeader>
												{(day, ...props) => {
													const mappedDay = getDayString(day);
													return (
														<CalendarHeaderCell style={{ color: "var(--blue)" }}>{_any(mappedDay)}</CalendarHeaderCell>
													);
												}}
											</CalendarGridHeader>
											<CalendarGridBody>{(date) => <CalendarCell date={date} />}</CalendarGridBody>
										</CalendarGrid>
									</div>
								</>
							);
						}}
					></RangeCalendar>
					<div className="calendarBottom">
						<span>{`${formatDate(range.start)} - ${formatDate(range.end)}`}</span>
						<Button className="calendarButton-cancel" onPress={handleCancel}>
							{_("btn.cancel")}
						</Button>
						<Button className="calendarButton-apply" onPress={handleApply}>
							{_("daterange.applyLabel")}
						</Button>
					</div>
				</div>
			)}
		</div>
	);
}

export default function DateRangeCalendar({ placeholder, locale, start, end, onChange, ...props }: DateRangeCalendarProps) {
	return (
		<I18nProvider locale={locale}>
			<BaseRangeCalendar placeholder={placeholder} start={start} end={end} onChange={onChange} {...props} />
		</I18nProvider>
	);
}
